// Day Book — single-day cash + bank reconciliation. Opening + receipts (by
// method) + disbursements + closing. Receipts come from subscription_ledger
// payments; cash_book provides the manual entries (opening, payouts,
// expenses, adjustments, bank in/out).

import { useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr, today_ymd_ist } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';
import { DateFilterBar } from '../components/DateFilterBar.js';

interface DayBookData {
  date: string;
  opening_paise: number;
  closing_paise: number;
  by_method: Array<{ method: string; total_paise: number; count: number }>;
  receipts: Array<{
    timestamp: string;
    method: string | null;
    amount_paise: number;
    subscriber_id: string | null;
    subscriber_name_en: string | null;
    subscriber_name_ta: string | null;
    chit_name_en: string | null;
    chit_name_ta: string | null;
    reference: string | null;
  }>;
  disbursements: Array<{
    timestamp: string;
    kind: string;
    amount_paise: number;
    note: string | null;
  }>;
  all_entries: unknown[];
  totals: {
    receipts_paise: number;
    payouts_paise: number;
    expenses_paise: number;
    adjustments_paise: number;
    bank_in_paise: number;
    bank_out_paise: number;
  };
}

const METHOD_COLOR: Record<string, string> = {
  upi: 'var(--primary)',
  cash: 'var(--good)',
  cheque: 'var(--warn)',
  neft: 'var(--info)',
  bank_transfer: 'var(--ink-3)',
};

export function ReportsDayBookPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [date, set_date] = useState(today_ymd_ist());

  const q = useQuery({
    queryKey: ['report-day-book', date],
    queryFn: () => orpc.reports.day_book({ date }) as Promise<DayBookData>,
  });
  const data = q.data;

  const export_csv = (): void => {
    if (!data) return;
    const lines = [
      'timestamp,kind,method,amount_rupees,subscriber,chit,reference,note',
      ...data.receipts.map((r) =>
        [
          r.timestamp,
          'receipt',
          r.method ?? '',
          (r.amount_paise / 100).toFixed(2),
          ((r.subscriber_name_en || r.subscriber_name_ta) ?? '').replace(/,/g, ' '),
          ((r.chit_name_en || r.chit_name_ta) ?? '').replace(/,/g, ' '),
          (r.reference ?? '').replace(/,/g, ' '),
          '',
        ].join(','),
      ),
      ...data.disbursements.map((d) =>
        [
          d.timestamp,
          d.kind,
          '',
          (Math.abs(d.amount_paise) / 100).toFixed(2),
          '',
          '',
          '',
          (d.note ?? '').replace(/,/g, ' '),
        ].join(','),
      ),
    ];
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `day-book-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.day_book_pdf({ date }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  const channel_total = data?.by_method.reduce((s, m) => s + m.total_paise, 0) ?? 0;

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('தினசரி கணக்கு', 'Day Book')}</div>
          <div className="h-sub">{date}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <DateFilterBar mode="single" value={date} on_change={set_date} />
          <button type="button" className="btn" onClick={export_csv} disabled={!data}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      <div className="kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="kpi"><div className="kpi-label">{t('தொடக்க இருப்பு', 'Opening balance')}</div><div className="kpi-value">{data ? format_inr(data.opening_paise) : '—'}</div></div>
        <div className="kpi"><div className="kpi-label">{t('வசூல்', 'Receipts')}</div><div className="kpi-value">{data ? format_inr(data.totals.receipts_paise) : '—'}</div><div className="kpi-sub">{data ? `${data.receipts.length} ${t('கொடுப்பனவுகள்', 'payments')}` : ''}</div></div>
        <div className={'kpi' + ((data?.totals.payouts_paise ?? 0) + (data?.totals.expenses_paise ?? 0) > 0 ? ' warn' : '')}>
          <div className="kpi-label">{t('வழங்கல்', 'Payouts + expenses')}</div>
          <div className="kpi-value">{data ? format_inr(data.totals.payouts_paise + data.totals.expenses_paise) : '—'}</div>
        </div>
        <div className="kpi primary"><div className="kpi-label">{t('இறுதி இருப்பு', 'Closing balance')}</div><div className="kpi-value">{data ? format_inr(data.closing_paise) : '—'}</div></div>
      </div>

      {q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(q.error)}</div>}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {/* Receipts by method */}
          <div className="card">
            <div className="card-head">
              <div className="card-title">{t('வசூல் — வழி வாரியாக', 'Receipts by method')}</div>
              <div className="card-sub mono">{format_inr(channel_total)}</div>
            </div>
            <div style={{ padding: 14 }}>
              {data.by_method.length === 0 && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{t('வசூல் இல்லை', 'No receipts')}</div>}
              {data.by_method.map((m) => (
                <div key={m.method} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: METHOD_COLOR[m.method] ?? 'var(--ink-3)' }} />
                  <span style={{ flex: 1, textTransform: 'uppercase' }} className="mono">{m.method}</span>
                  <span className="mono">{m.count}</span>
                  <span className="mono" style={{ minWidth: 80, textAlign: 'right' }}>{format_inr(m.total_paise)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Disbursements + cash book entries */}
          <div className="card">
            <div className="card-head">
              <div className="card-title">{t('வழங்கல்கள்', 'Disbursements & adjustments')}</div>
              <div className="card-sub mono">{data.disbursements.length}</div>
            </div>
            <div style={{ padding: 14 }}>
              {data.disbursements.length === 0 && <div style={{ color: 'var(--ink-3)', fontSize: 13 }}>{t('வழங்கல் இல்லை', 'No disbursements')}</div>}
              {data.disbursements.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', fontSize: 13, borderBottom: '1px solid var(--line)' }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 50 }}>{new Date(d.timestamp).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
                  <span style={{ flex: 1 }}>{d.kind}{d.note ? ` — ${d.note}` : ''}</span>
                  <span className="mono" style={{ color: 'var(--bad)' }}>{format_inr(Math.abs(d.amount_paise))}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {data && data.receipts.length > 0 && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-head">
            <div className="card-title">{t('பெற்ற கொடுப்பனவுகள்', 'Receipts log')}</div>
            <div className="card-sub mono">{data.receipts.length}</div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead style={{ background: 'var(--bg-elev)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('நேரம்', 'Time')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('உறுப்பினர்', 'Member')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('சீட்டு', 'Chit')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('வழி', 'Method')}</th>
                  <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('குறிப்பு எண்', 'Reference')}</th>
                  <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('தொகை', 'Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {data.receipts.map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={{ padding: '6px 10px' }} className="mono">{new Date(r.timestamp).toLocaleTimeString(lang === 'ta' ? 'ta-IN' : 'en-IN', { hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                      {r.subscriber_id ? (
                        <Link to={`/subscribers/${r.subscriber_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                          {(lang === 'ta' ? r.subscriber_name_ta : r.subscriber_name_en) || '—'}
                        </Link>
                      ) : '—'}
                    </td>
                    <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>{(lang === 'ta' ? r.chit_name_ta : r.chit_name_en) || '—'}</td>
                    <td style={{ padding: '6px 10px' }}><span className="mono" style={{ textTransform: 'uppercase', fontSize: 11 }}>{r.method ?? '—'}</span></td>
                    <td style={{ padding: '6px 10px', color: 'var(--ink-3)', fontSize: 11 }}>{r.reference ?? '—'}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--good)' }} className="mono">{format_inr(r.amount_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 960, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="db-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
